import { Component, OnInit } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { of, Subject } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { Guid } from "guid-typescript";
import { AppConstants } from "src/app/constants/constants";
import { Roles } from "src/app/constants/Roles";
import { UserProfileService } from "src/app/_services/userprofile.service";
import { UserSessionService } from "src/app/_services/user-session.service";
import { GetAdministratorRoles } from "src/app/_store/role/role.actions";
import { selectGetAdministratorRoles } from "src/app/_store/role/role.selectors";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Role } from "src/app/_models/Role";
import { SaveLoginHistory } from "src/app/_store/event-history/event-history.action";
import { AddLoginHistory } from "src/app/_models/EventDto";
import { selectOwnUserProfile } from "src/app/_store/user-profile/user-profile.selectors";
import { Userprofile } from "src/app/_models/Userprofile";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit {
  loginDisplay = false;
  displayedColumns: string[] = ["claim", "value"];
  dataSource: any = [];
  userDataSource: any = [];
  subjectId: Guid;
  role: string = AppConstants.BLANK;
  adminRole: number[] = [];
  readonly learnerRole: number[] = [Roles.Learner];
  readonly placeholderDashboardRoles: number[] = [
    Roles.Guest,
    Roles.ContentAdministrator
  ];
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userProfileService: UserProfileService,
    private userSessionService: UserSessionService,
    private router: Router,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.userSessionService.onStartUpDisplayLoader$.next(true);
    this.getAdministratorRoles();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) =>
            status === InteractionStatus.None ||
            status === InteractionStatus.HandleRedirect
        ),
        switchMap(() => this.userSessionService.isUserNameActive$()),
        switchMap((data) => {
          if (data) {
            return of(null);
          }

          return this.userProfileService
            .getOwnUserProfile$()
            .pipe(
              switchMap((data) =>
                this.userSessionService.setUsername$(data.username)
              )
            );
        })
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.getClaims(
          this.authService.instance.getActiveAccount()?.idTokenClaims
        );
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);

      this.store.select(selectOwnUserProfile)
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: Userprofile) => {
        if(result) {
          this.saveLoginRecord(result);
        }
      });
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  getClaims(claims: any) {
    this.subjectId = Guid.parse(claims["sub"]);

    this.dataSource = [
      { id: 1, claim: "Subject ID", value: claims ? claims["sub"] : null },
      { id: 2, claim: "Tenant ID", value: claims ? claims["tid"] : null },
      { id: 3, claim: "Issuer", value: claims ? claims["iss"] : null },
      { id: 4, claim: "Username", value: claims ? claims["signinname"] : null },
      {
        id: 5,
        claim: "AuthSource",
        value: claims ? claims["authenticationSource"] : null,
      },
    ];
  }

  getAdministratorRoles() : void {
    this.store.select(selectGetAdministratorRoles)
      .pipe(takeUntil(this._destroying$))
      .subscribe((arResult: Role[]) => {
        this.adminRole = arResult === null ? [] : arResult.map(ar => ar.id);

        if(this.adminRole.length > 0) {
          this.reRouteUser();
        }
      });
    
    this.store.dispatch(GetAdministratorRoles());
  }

  reRouteUser(): void {
    this.userProfileService.getOwnUserProfile$().subscribe(
      (result) => {

        var roleId = result[AppConstants.ROLE_ID];
        if (this.adminRole.indexOf(roleId) > -1) {
          this.router.navigate([AppConstants.ADMIN_PAGE]),
            {
              queryParamsHandling: AppConstants.MERGE,
              relativeTo: this.router,
            };
          this.userSessionService.onStartUpDisplayLoader$.next(false);
        }
        else if(roleId === Roles.Tutor)
        {
          this.router.navigate([AppConstants.TUTOR_PAGE]),
          {
            queryParamsHandling: AppConstants.MERGE,
            relativeTo: this.router,
          };
          this.userSessionService.onStartUpDisplayLoader$.next(false);
        }
      },
      (error) => {
        console.error(error);
        this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        this.userSessionService.onStartUpDisplayLoader$.next(false);
      }
    );
  }

  saveLoginRecord(userProfile: Userprofile): void {
    //Create Login Record History Data
    var loginRecord:  AddLoginHistory = {
      currentUserId: userProfile?.id
    };
    
    this.store.dispatch(SaveLoginHistory({loginHistory: loginRecord}));
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
